/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useEffect } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	DynamicSurfaceView,
	useAIContext,
	useAICurrentView,
	useAISurfaceMetadata,
	useViewAIActions,
} from '@atlassian/ai-smart-button-api';
import { useProfileController } from '@atlassian/conversation-assistant-store';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useRovoButtonEventsInstrumentation } from '../../hooks/useRovoButtonEventsInstrumentation';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIActions } from '../ai-actions';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIFeatureView } from '../ai-feature-view';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { ObjectSidebar } from '../common/types/object-sidebar-types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { AISmartButtonSidebarAPI } from '../common/types/shared-types';

import { AIDynamicSurfaceContextProvider } from './AIDynamicSurfaceContext';
import { AIDynamicSurfaceHeader } from './AIDynamicSurfaceHeader';
import { useUpdateDynamicSurfacePanelHeaderComponents } from './useDynamicSurfacePanel';

export type InternalViews = 'dynamic-surface';
export type AIDynamicSurfaceProps = {
	/**
	 * Use sidebarApi instead. objectSidebar will be removed in a future release.
	 */
	sidebarApi: AISmartButtonSidebarAPI | ObjectSidebar;
};

const styles = cssMap({
	container: {
		height: '100%',
		backgroundColor: token('color.background.input'),
	},
});

export const AIDynamicSurfaceInner = ({ sidebarApi }: AIDynamicSurfaceProps) => {
	const usesLegacyObjectSidebar = 'actions' in sidebarApi;

	const { changeView } = useViewAIActions();
	const currentView = useAICurrentView();
	const profileData = useProfileController();
	const name = profileData.name.split(' ')[0];
	const context = useAIContext();
	const shouldHavePanelTitle = !!context?.reference;
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const surfaceMetadata = useAISurfaceMetadata();

	useUpdateDynamicSurfacePanelHeaderComponents(usesLegacyObjectSidebar ? sidebarApi : undefined);

	useEffect(() => {
		return () => {
			changeView(DynamicSurfaceView.Default);
		};
	}, [changeView]);

	useEffect(() => {
		fireRovoButtonUIEvent({
			source: surfaceMetadata?.contentType,
			action: 'rendered',
			actionSubject: 'rovoButton',
			actionSubjectId: 'dynamicSurfacePanel',
			attributes: {
				product: surfaceMetadata?.product,
				productPage: surfaceMetadata?.contentType,
				pageMode: surfaceMetadata?.pageMode,
				menuItemId: currentView !== DynamicSurfaceView.Default ? currentView?.id : 'default',
				menuItemType: currentView !== DynamicSurfaceView.Default ? currentView?.type : 'default',
			},
			product: surfaceMetadata?.product,
		});
	}, [fireRovoButtonUIEvent, surfaceMetadata, currentView]);

	if (currentView === DynamicSurfaceView.Default) {
		return (
			<Stack grow="fill" testId="ai-dynamic-surface" xcss={styles.container}>
				{!usesLegacyObjectSidebar && (
					<AIDynamicSurfaceHeader
						sidebarApi={sidebarApi}
						isDefaultView={true}
						shouldHavePanelTitle={shouldHavePanelTitle}
					/>
				)}
				{/* AIAction will render both actions and studio sections */}
				<AIActions name={name} sidebarApi={sidebarApi} />
			</Stack>
		);
	}

	return (
		<>
			{!usesLegacyObjectSidebar && (
				<AIDynamicSurfaceHeader
					sidebarApi={sidebarApi}
					isDefaultView={false}
					shouldHavePanelTitle={shouldHavePanelTitle}
				/>
			)}
			<AIFeatureView sidebarApi={sidebarApi} view={currentView?.viewConfig} />
		</>
	);
};

export const AIDynamicSurface = ({ sidebarApi }: AIDynamicSurfaceProps) => {
	return (
		<AIDynamicSurfaceContextProvider>
			<AIDynamicSurfaceInner sidebarApi={sidebarApi} />
		</AIDynamicSurfaceContextProvider>
	);
};
